import { Component, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs';
import { format, parseISO } from 'date-fns';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { SocBulletin } from '../../../../../../../core/models/entities';
import { environment } from '../../../../../../../../environments/environment';
import { SocBulletinForm, SocBulletinFormService } from './service/bulletin-form.service';
import { SocBulletinRequest, SocBulletinService } from './service/bulletin.service';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-bulletin',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DateInputsModule,
    DialogModule,
    ReactiveFormsModule,
    DatatableComponent,
  ],
  templateUrl: './bulletin.component.html',
  styleUrl: './bulletin.component.scss',
})
export class BulletinComponent extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public gridService = inject(SocBulletinService);
  public route = inject(ActivatedRoute);
  private formService = inject(SocBulletinFormService);
  private notificationService = inject(NotificationService);

  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/bulletin`;
  public id!: number;

  public datatableUrl = signal<string>('');
  public formGroup = signal<FormGroup<SocBulletinForm> | undefined>(undefined);
  public isNew = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  private rows = new Map<string, SocBulletin>();

  public datatableColumns = [
    {
      data: 'xdr_alert_type',
      name: 'xdr_alert_type',
      title: 'XDR Alert',
      type: 'string',
      render: DataTable.render.text(),
    },
    { data: 'description', name: 'description', title: 'Description', type: 'string', render: DataTable.render.text() },
    {
      data: 'required_action',
      name: 'required_action',
      title: 'Required Action',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: 'timebounds',
      name: 'timebounds',
      title: 'Expiration Date',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: null,
      name: 'actions',
      title: 'Action',
      type: 'actions',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: SocBulletin) => {
        this.rows.set(String(row.pk), row);
        return `
          <div class="d-flex gap-2">
            <button type="button" class="cyflare-btn-secondary cyflare-btn-sm action-edit" data-pk="${row.pk}">
              Edit
            </button>
            <button type="button" class="cyflare-btn-danger cyflare-btn-sm action-remove" data-pk="${row.pk}">
              Remove
            </button>
          </div>
        `;
      },
    },
  ];

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams.pipe(filter((params) => params['organizationId'])).subscribe((data) => {
          this.id = parseInt(data['organizationId'], 10);
          // Still the source of truth for update and delete, which build their
          // endpoint from it, even though the grid no longer reads through the
          // service.
          this.gridService.url = `${this.baseUrl}(${this.id})/`;
          this.rows.clear();
          this.datatableUrl.set(this.gridService.url);
        });
      },
      { allowSignalWrites: true }
    );
  }

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest('button');
    const pk = button?.getAttribute('data-pk');
    if (!button || !pk) {
      return;
    }

    if (button.classList.contains('action-edit')) {
      this.openEditor(this.rows.get(pk));
    } else if (button.classList.contains('action-remove')) {
      this.remove(pk);
    }
  }

  public openEditor(bulletin?: SocBulletin): void {
    this.isNew.set(!bulletin);
    // The date picker works with Date objects while the model types timebounds
    // as a string, so the value is converted on the way in and formatted back
    // on save. Same conversion the grid editor did.
    const dataItem = bulletin
      ? ({
          ...bulletin,
          timebounds: bulletin.timebounds ? parseISO(bulletin.timebounds) : null,
        } as unknown as SocBulletin)
      : undefined;
    this.formGroup.set(this.formService.createSocBulletinForm(dataItem));
  }

  public closeEditor(): void {
    // The dialog close button and the backdrop both land here; ignore them
    // while a save is in flight so the user cannot walk away mid-request.
    if (this.isSaving()) {
      return;
    }
    this.formGroup.set(undefined);
  }

  public save(): void {
    const formGroup = this.formGroup();
    if (!formGroup || this.isSaving()) {
      return;
    }
    const formValue = formGroup.value;
    const timebounds = formValue.timebounds ? new Date(formValue.timebounds) : null;

    const payload: SocBulletinRequest = {
      organization_id: this.id,
      soc_response_bulletin: {
        pk: this.isNew() ? null : formValue?.pk!,
        description: formValue?.description!,
        xdr_alert_type: formValue?.xdr_alert_type!,
        required_action: formValue?.required_action!,
        timebounds: timebounds ? format(timebounds, 'yyyy-MM-dd HH:mm:ss') : null,
      },
    };

    const request = this.isNew()
      ? this.gridService.createSocBulletin(payload)
      : this.gridService.updateSocBulletin(payload.soc_response_bulletin);

    this.isSaving.set(true);
    request.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeEditor();
        this.table?.reload();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.showError('Failed to save the SOC bulletin.');
      },
    });
  }

  private remove(pk: string): void {
    this.gridService.deleteSocBulletin(Number(pk)).subscribe({
      next: () => {
        this.table?.reload();
      },
      error: (error) => {
        console.error(error);
        this.showError('Failed to delete the SOC bulletin.');
      },
    });
  }

  private showError(message: string): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-error',
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: true,
      hideAfter: 5000,
    });
  }
}
